import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, ChevronDown, X } from "lucide-react";
import { NAV_GROUPS, MAIN_LINKS } from "../../lib/navigation";
import { LOGO } from "../../lib/images";
import SearchDialog from "./SearchDialog";
import MemberMenu from "./MemberMenu";

function Logo({ onClick }) {
  return (
    <Link
      to="/"
      onClick={onClick}
      className="flex items-center gap-3 group"
      data-testid="logo-home-link"
    >
      <img
        src={LOGO}
        alt="ÀsÀNigHT Worldwide"
        className="w-11 h-11 rounded-full object-cover ring-1 ring-gold/40 group-hover:ring-gold transition-all"
      />
      <span className="leading-none">
        <span className="block font-serif text-xl text-ink tracking-tight">
          ÀsÀNigHT
        </span>
        <span className="block text-[10px] uppercase tracking-[0.3em] text-ink-muted">
          Worldwide
        </span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeGroup, setActiveGroup] = React.useState(null);
  const location = useLocation();

  React.useEffect(() => {
    setMobileOpen(false);
    setActiveGroup(null);
  }, [location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-50 bg-cream/80 backdrop-blur-xl border-b border-parchment">
        <div className="bg-forest text-center py-1.5">
          <p className="font-serif italic text-sm text-gold-soft tracking-wide" data-testid="brand-motto">
            Culture is the total way of life…
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            <Logo />

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_GROUPS.map((group) => (
                <div
                  key={group.label}
                  className="relative"
                  onMouseEnter={() => setActiveGroup(group.label)}
                  onMouseLeave={() => setActiveGroup(null)}
                >
                  <button
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-ink hover:text-forest transition-colors whitespace-nowrap"
                    data-testid={`nav-group-${group.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {group.label}
                    <ChevronDown className="w-3.5 h-3.5 mt-0.5" />
                  </button>
                  {activeGroup === group.label && (
                    <div className="absolute left-0 top-full pt-3 w-[340px]">
                      <div className="bg-cream border border-parchment rounded-lg shadow-xl shadow-ink/5 p-3 animate-fade-up">
                        <p className="px-3 pt-2 pb-3 text-xs uppercase tracking-widest text-gold">
                          {group.description}
                        </p>
                        <div className="grid gap-0.5">
                          {group.items.map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              className="px-3 py-2 rounded-md text-sm text-ink-muted hover:bg-sand hover:text-forest transition-colors"
                              data-testid={`nav-link-${item.to}`}
                            >
                              {item.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {MAIN_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive ? "text-forest" : "text-ink hover:text-forest"
                    }`
                  }
                  data-testid={`nav-link-${l.to}`}
                >
                  {l.title}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 text-sm text-ink-muted border border-parchment rounded-full px-3.5 py-2 hover:border-gold hover:text-forest transition-colors"
                data-testid="search-trigger-button"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Search</span>
              </button>
              <Link
                to="/topic/origins-and-history"
                className="hidden md:inline-flex bg-forest text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors"
                data-testid="start-learning-nav-button"
              >
                Start Learning
              </Link>
              <div className="hidden md:block"><MemberMenu /></div>
              <button
                onClick={() => setMobileOpen((o) => !o)}
                className="lg:hidden grid place-items-center w-10 h-10 text-ink"
                data-testid="mobile-menu-button"
                aria-label="Menu"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-parchment bg-cream max-h-[80vh] overflow-y-auto">
            <div className="px-5 py-4 space-y-5">
              {NAV_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="text-xs uppercase tracking-widest text-gold mb-2">
                    {group.label}
                  </p>
                  <div className="grid gap-1">
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="py-2 text-ink-muted hover:text-forest"
                        data-testid={`mobile-nav-link-${item.to}`}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <div className="border-t border-parchment pt-4 grid gap-1">
                {MAIN_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} className="py-2 text-ink font-medium">
                    {l.title}
                  </Link>
                ))}
              </div>
              <div className="border-t border-parchment pt-4"><MemberMenu mobile /></div>
            </div>
          </div>
        )}
      </header>
      <SearchDialog open={searchOpen} setOpen={setSearchOpen} />
      {/* expose opener via custom event for footer/hero */}
      <SearchOpenerBridge setOpen={setSearchOpen} />
    </>
  );
}

function SearchOpenerBridge({ setOpen }) {
  React.useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("asa:open-search", handler);
    return () => window.removeEventListener("asa:open-search", handler);
  }, [setOpen]);
  return null;
}
